import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import type { PortalPresencePortal } from './portalPresence';
import { resolveActivePortalKind } from './portal';

export type PortalActivityAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'approve'
  | 'reject'
  | 'transfer'
  | 'online'
  | 'offline';

export type PortalActivityEntity =
  | 'account'
  | 'contact'
  | 'project'
  | 'task'
  | 'meeting_minutes'
  | 'transfer'
  | 'allowlist'
  | 'sharepoint_settings'
  | 'presence';

export type PortalActivityRow = {
  id: string;
  portal: PortalPresencePortal;
  action: PortalActivityAction | string;
  entity_type: PortalActivityEntity | string;
  entity_id: string | null;
  client_id: number | null;
  actor_id: string;
  actor_name: string | null;
  actor_email: string | null;
  title: string;
  detail: string;
  metadata: Record<string, unknown>;
  created_at: string;
};

export const PORTAL_ACTIVITY_ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  approve: 'Approved',
  reject: 'Rejected',
  transfer: 'Transfer',
  online: 'Online',
  offline: 'Offline',
};

export const PORTAL_ACTIVITY_ENTITY_LABELS: Record<string, string> = {
  account: 'Account',
  contact: 'Contact',
  project: 'Project',
  task: 'Task',
  meeting_minutes: 'Meeting minutes',
  transfer: 'Transfer',
  allowlist: 'User access',
  sharepoint_settings: 'SharePoint',
  presence: 'Session',
};

export const PORTAL_ACTIVITY_PAGE_SIZE = 25;

export type LogPortalActivityInput = {
  portal?: PortalPresencePortal;
  action: PortalActivityAction;
  entityType: PortalActivityEntity;
  title: string;
  detail?: string;
  entityId?: string | number | null;
  clientId?: string | number | null;
  metadata?: Record<string, unknown>;
};

export function activityChangeLine(label: string, before: string, after: string): string | null {
  if (before === after) return null;
  const from = before || '—';
  const to = after || '—';
  return `${label}: ${from} → ${to}`;
}

export function buildActivityChangeDetail(
  lines: Array<string | null | undefined>,
): string | null {
  const filtered = lines.filter((line): line is string => Boolean(line));
  if (filtered.length === 0) return null;
  return filtered.join('\n');
}

export function resolveActivityPortal(): PortalPresencePortal {
  if (typeof window === 'undefined') return 'crm';
  const path = window.location.pathname;
  if (path === '/admin' || path.startsWith('/admin/')) return 'admin';
  return resolveActivePortalKind();
}

export async function logPortalActivity(input: LogPortalActivityInput): Promise<void> {
  const { error } = await getDbSupabase('crm').rpc('log_portal_activity_event', {
    p_portal: input.portal ?? resolveActivityPortal(),
    p_action: input.action,
    p_entity_type: input.entityType,
    p_title: input.title,
    p_detail: input.detail ?? '',
    p_entity_id: input.entityId != null ? String(input.entityId) : null,
    p_client_id: input.clientId != null ? Number(input.clientId) : null,
    p_metadata: input.metadata ?? {},
  });
  if (error) throw new Error(formatSupabaseError(error));
}

export function logPortalActivityQuiet(input: LogPortalActivityInput): void {
  void logPortalActivity(input).catch(() => {
    /* audit must not block user actions */
  });
}

export type FetchPortalActivityOptions = {
  limit?: number;
  offset?: number;
  portal?: PortalPresencePortal | '';
  action?: PortalActivityAction | '';
  search?: string;
};

export async function fetchPortalActivityLog(
  options: FetchPortalActivityOptions = {},
): Promise<PortalActivityRow[]> {
  const { data, error } = await getDbSupabase('crm').rpc('list_portal_activity_log', {
    p_limit: options.limit ?? PORTAL_ACTIVITY_PAGE_SIZE,
    p_offset: options.offset ?? 0,
    p_portal: options.portal || null,
    p_action: options.action || null,
    p_search: options.search?.trim() || null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as PortalActivityRow[];
}

export async function fetchPortalActivityLogCount(
  options: Omit<FetchPortalActivityOptions, 'limit' | 'offset'> = {},
): Promise<number> {
  const { data, error } = await getDbSupabase('crm').rpc('count_portal_activity_log', {
    p_portal: options.portal || null,
    p_action: options.action || null,
    p_search: options.search?.trim() || null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return Number(data ?? 0);
}

export function portalActivityActionLabel(action: string): string {
  return PORTAL_ACTIVITY_ACTION_LABELS[action] ?? action;
}

export function portalActivityEntityLabel(entityType: string): string {
  return PORTAL_ACTIVITY_ENTITY_LABELS[entityType] ?? entityType;
}

export function portalActivityActionClass(action: string): string {
  return `activity-log__action activity-log__action--${action}`;
}
